import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import Cookies from "js-cookie";

import { API_APPROVAL_DASHBOARD } from "../../../../../config/Endpoints.js";
import { showError } from "../../../../utils/toast.ts";
import type {
  ApprovalDashboardFilters,
  ApprovalDashboardPage,
  ApproverGroup,
} from "../types.ts";

interface RawDashboardResponse {
  content?: ApproverGroup[];
  totalElements?: number;
  totalPages?: number;
  size?: number;
  number?: number;
  last?: boolean;
  first?: boolean;
}

function buildParams(
  filters: ApprovalDashboardFilters,
  page: number,
  size: number,
): Record<string, string | number> {
  const params: Record<string, string | number> = {
    page: Math.max(0, page - 1),
    size,
    status: filters.status,
    approverStatus: "false",
  };
  if (filters.approverId != null) params.approverId = filters.approverId;
  if (filters.employeeId != null) params.employeeId = filters.employeeId;
  if (filters.region) params.region = filters.region;
  if (filters.fromDate) params.fromDate = filters.fromDate;
  if (filters.toDate) params.toDate = filters.toDate;
  return params;
}

function getApiErrorMessage(err: unknown, fallback: string): string {
  if (axios.isAxiosError(err)) {
    const msg = err.response?.data?.message;
    if (typeof msg === "string" && msg.trim() !== "") return msg;
  }
  if (err instanceof Error && err.message) return err.message;
  return fallback;
}

export function useApprovalDashboard(
  filters: ApprovalDashboardFilters,
  page: number,
  size: number,
) {
  const [data, setData] = useState<ApprovalDashboardPage>({
    groups: [],
    totalElements: 0,
    totalPages: 0,
    size,
    number: 0,
    isLastPage: true,
    isFirstPage: true,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const token = Cookies.get("accessToken");

  const fetchDashboard = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get<RawDashboardResponse>(API_APPROVAL_DASHBOARD, {
        params: buildParams(filters, page, size),
        headers: { Authorization: `Bearer ${token}` },
      });
      const body = res.data ?? {};
      const resolvedNumber =
        typeof body.number === "number" ? body.number : page - 1;
      const resolvedTotalPages =
        typeof body.totalPages === "number" ? body.totalPages : 0;
      const resolvedSize =
        typeof body.size === "number" ? body.size : size;
      setData({
        groups: Array.isArray(body.content) ? body.content : [],
        totalElements:
          typeof body.totalElements === "number" ? body.totalElements : 0,
        totalPages: resolvedTotalPages,
        size: resolvedSize,
        number: resolvedNumber,
        isLastPage:
          typeof body.last === "boolean"
            ? body.last
            : resolvedNumber + 1 >= Math.max(resolvedTotalPages, 1),
        isFirstPage:
          typeof body.first === "boolean"
            ? body.first
            : resolvedNumber <= 0,
      });
    } catch (err) {
      const msg = getApiErrorMessage(
        err,
        "Failed to load approval dashboard.",
      );
      setData({
        groups: [],
        totalElements: 0,
        totalPages: 0,
        size,
        number: 0,
        isLastPage: true,
        isFirstPage: true,
      });
      setError(msg);
      showError(msg);
    } finally {
      setLoading(false);
    }
  }, [filters, page, size, token]);

  useEffect(() => {
    void fetchDashboard();
  }, [fetchDashboard]);

  return {
    groups: data.groups,
    totalElements: data.totalElements,
    totalPages: data.totalPages,
    pageNumber: data.number,
    pageSize: data.size,
    isLastPage: data.isLastPage,
    isFirstPage: data.isFirstPage,
    loading,
    error,
    refetch: fetchDashboard,
  };
}
